import { motion } from 'framer-motion'

interface SkeletonProps {
  className?: string
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded'
  width?: string | number
  height?: string | number
  animation?: 'pulse' | 'wave' | 'none'
}

export function Skeleton({
  className = '',
  variant = 'text',
  width,
  height,
  animation = 'pulse',
}: SkeletonProps) {
  const baseClasses = 'bg-bg-card'

  const variantClasses = {
    text: 'rounded h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-none',
    rounded: 'rounded-xl',
  }

  const animationClasses = {
    pulse: 'animate-pulse',
    wave: 'animate-shimmer',
    none: '',
  }

  const style: React.CSSProperties = {}
  if (width) style.width = typeof width === 'number' ? `${width}px` : width
  if (height) style.height = typeof height === 'number' ? `${height}px` : height

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${animationClasses[animation]} ${className}`}
      style={style}
    />
  )
}

// Pre-built skeleton layouts
export function SkeletonCard() {
  return (
    <div className="p-4 bg-bg-card rounded-2xl border border-white/5 space-y-3">
      <div className="flex items-center gap-3">
        <Skeleton variant="circular" width={40} height={40} />
        <div className="flex-1 space-y-2">
          <Skeleton width="60%" height={16} />
          <Skeleton width="40%" height={12} />
        </div>
      </div>
    </div>
  )
}

export function SkeletonTaskCard() {
  return (
    <div className="p-4 bg-bg-card rounded-2xl border border-white/5">
      <div className="flex items-start gap-3">
        <Skeleton variant="rounded" width={28} height={28} />
        <div className="flex-1 space-y-2">
          <Skeleton width="70%" height={18} />
          <Skeleton width="40%" height={14} />
        </div>
        <Skeleton variant="rounded" width={60} height={28} />
      </div>
    </div>
  )
}

export function SkeletonCharacterDisplay() {
  return (
    <div className="bg-gradient-to-b from-bg-surface to-bg-dark rounded-3xl p-4">
      <div className="flex items-center justify-center mb-4">
        <Skeleton variant="rounded" width={160} height={176} />
      </div>
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between">
            <Skeleton width={80} height={14} />
            <Skeleton width={100} height={14} />
          </div>
          <Skeleton variant="rounded" width="100%" height={20} />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton width={60} height={14} />
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="circular" width={18} height={18} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export function SkeletonList({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {[...Array(count)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: i * 0.1 }}
        >
          <SkeletonTaskCard />
        </motion.div>
      ))}
    </div>
  )
}
